// src/components/primitives.js — shared Flasher primitives.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

const SCRAMBLE_CHARS = '!<>-_\\/[]{}—=+*^?#________';

export function useScramble(target, { duration = 800, trigger = true } = {}) {
  const [output, setOutput] = useState(target);
  const rafRef = useRef(null);
  const startRef = useRef(0);

  const run = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (document.body.dataset.motion === 'reduced') {
      setOutput(target);
      return;
    }
    startRef.current = performance.now();
    const len = target.length;
    const tick = (t) => {
      const p = Math.min(1, (t - startRef.current) / duration);
      let out = '';
      for (let i = 0; i < len; i++) {
        const charP = i / len;
        if (target[i] === ' ') {
          out += ' ';
        } else if (p > charP + 0.15) {
          out += target[i];
        } else {
          out += SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
        }
      }
      setOutput(out);
      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setOutput(target);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [target, duration]);

  useEffect(() => {
    if (trigger) run();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [trigger, run]);

  return { output, run };
}

export function ScrambleText({ children, duration = 900, as: Tag = 'span', className, style, hoverable = true }) {
  const { output, run } = useScramble(String(children), { duration });
  // nowrap keeps the layout stable while scrambling — punctuation in the
  // scramble alphabet otherwise introduces line-break opportunities that make
  // the headline grow taller mid-animation and shove following lines down.
  return (
    <Tag
      className={className}
      style={{ display: 'inline-block', whiteSpace: 'nowrap', ...style }}
      onMouseEnter={hoverable ? run : undefined}
    >
      {output}
    </Tag>
  );
}

export function RotatingWord({ words, interval = 2400 }) {
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState('in');

  useEffect(() => {
    let outT;
    const cycle = setInterval(() => {
      if (document.body.dataset.motion === 'reduced') {
        setIdx((i) => (i + 1) % words.length);
        return;
      }
      setPhase('out');
      outT = setTimeout(() => {
        setIdx((i) => (i + 1) % words.length);
        setPhase('in');
      }, 280);
    }, interval);
    return () => {
      clearInterval(cycle);
      clearTimeout(outT);
    };
  }, [words.length, interval]);

  const word = words[idx];
  // The gradient + bg-clip:text styling lives on the inner span (the one that
  // also carries the transform). Putting it on the outer span breaks the
  // gradient because the inner span's transform creates its own compositing
  // layer that the outer's bg-clip can't reach into.
  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-block',
        verticalAlign: 'baseline',
        minWidth: '4ch',
        paddingRight: '0.1em',
        paddingBottom: '0.18em',
        lineHeight: 1.05,
      }}
    >
      <span
        key={idx + phase}
        style={{
          display: 'inline-block',
          transform: phase === 'out' ? 'translateY(40%) rotateX(-60deg)' : 'translateY(0) rotateX(0)',
          opacity: phase === 'out' ? 0 : 1,
          transition: 'transform 0.32s cubic-bezier(.6,.05,.3,1), opacity 0.32s',
          transformOrigin: '50% 100%',
          background: 'linear-gradient(135deg, var(--purple) 0%, var(--magenta) 50%, var(--cyan) 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          color: 'transparent',
          filter: 'drop-shadow(0 0 24px rgba(255,177,63,0.45))',
          paddingRight: '0.06em',
        }}
      >
        {word}
      </span>
    </span>
  );
}

export function ParallaxLayer({ depth = 0.05, children, style, className }) {
  const ref = useRef(null);
  useEffect(() => {
    if (document.body.dataset.motion === 'reduced') return;
    const onMove = (e) => {
      if (!ref.current) return;
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      const dx = (e.clientX - cx) * depth;
      const dy = (e.clientY - cy) * depth;
      ref.current.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, [depth]);
  return (
    <div
      ref={ref}
      className={className}
      style={{ willChange: 'transform', transition: 'transform 0.18s ease-out', ...style }}
    >
      {children}
    </div>
  );
}

export function MagneticButton({ children, href, to, onClick, accent = 'magenta', size = 'md', as }) {
  const ref = useRef(null);
  const [hover, setHover] = useState(false);
  const onMove = (e) => {
    if (!ref.current) return;
    if (document.body.dataset.motion === 'reduced') return;
    const r = ref.current.getBoundingClientRect();
    const x = e.clientX - r.left - r.width / 2;
    const y = e.clientY - r.top - r.height / 2;
    ref.current.style.transform = `translate(${x * 0.18}px, ${y * 0.25}px)`;
  };
  const onLeave = () => {
    setHover(false);
    if (ref.current) ref.current.style.transform = 'translate(0,0)';
  };
  const accentVar = `var(--${accent})`;
  const padding = size === 'lg' ? '18px 32px' : size === 'sm' ? '10px 18px' : '14px 26px';
  const fontSize = size === 'lg' ? 17 : size === 'sm' ? 13 : 15;
  const Tag = to ? Link : (as || (href ? 'a' : 'button'));
  const tagProps = to ? { to } : (href ? { href } : { type: 'button' });
  return (
    <Tag
      {...tagProps}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={onLeave}
      onMouseMove={onMove}
      ref={ref}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        padding,
        fontSize,
        fontWeight: 600,
        letterSpacing: '0.01em',
        color: '#0a0419',
        background: `linear-gradient(135deg, ${accentVar} 0%, var(--purple) 100%)`,
        border: 'none',
        borderRadius: 999,
        cursor: 'pointer',
        boxShadow: hover
          ? `0 0 0 1px ${accentVar}, 0 12px 40px -8px ${accentVar}, 0 0 60px ${accentVar}55`
          : `0 0 0 1px ${accentVar}, 0 6px 20px -8px ${accentVar}`,
        transition: 'box-shadow 0.2s ease, transform 0.18s cubic-bezier(.2,1,.2,1)',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Tag>
  );
}

export function GhostButton({ children, href, to, onClick, size = 'md', as }) {
  const padding = size === 'lg' ? '17px 30px' : '13px 24px';
  const fontSize = size === 'lg' ? 17 : 15;
  const Tag = to ? Link : (as || (href ? 'a' : 'button'));
  const tagProps = to ? { to } : (href ? { href } : { type: 'button' });
  return (
    <Tag
      {...tagProps}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        padding,
        fontSize,
        fontWeight: 500,
        color: 'var(--ink)',
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid var(--line-strong)',
        borderRadius: 999,
        cursor: 'pointer',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        transition: 'background 0.2s, border-color 0.2s, transform 0.2s',
        textDecoration: 'none',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(160,102,255,0.12)';
        e.currentTarget.style.borderColor = 'var(--purple)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
        e.currentTarget.style.borderColor = 'var(--line-strong)';
      }}
    >
      {children}
    </Tag>
  );
}

export function Marquee({ items, speed = 60, accent = 'var(--magenta)' }) {
  const tape = [...items, ...items, ...items];
  return (
    <div style={{
      width: '100%',
      overflow: 'hidden',
      borderTop: '1px solid var(--line)',
      borderBottom: '1px solid var(--line)',
      padding: '20px 0',
      background: 'linear-gradient(90deg, rgba(255,177,63,0.04), rgba(160,102,255,0.04))',
      maskImage: 'linear-gradient(90deg, transparent, black 6%, black 94%, transparent)',
      WebkitMaskImage: 'linear-gradient(90deg, transparent, black 6%, black 94%, transparent)',
    }}>
      <div
        className="marquee-track"
        style={{
          display: 'inline-flex',
          gap: 56,
          animation: `marquee ${speed}s linear infinite`,
          whiteSpace: 'nowrap',
        }}
      >
        {tape.map((it, i) => (
          <span key={i} style={{
            fontFamily: 'var(--f-display)',
            fontSize: 'clamp(28px, 4vw, 56px)',
            fontWeight: 600,
            letterSpacing: '-0.02em',
            color: i % 2 ? 'var(--ink)' : accent,
          }}>
            {it}
            <span style={{ marginLeft: 56, color: 'var(--ink-3)' }}>✦</span>
          </span>
        ))}
      </div>
      <style>{`@keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-33.333%); } }`}</style>
    </div>
  );
}

export function Eyebrow({ children, accent = 'var(--magenta)' }) {
  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 10,
      fontFamily: 'var(--f-mono)',
      fontSize: 12,
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      color: 'var(--ink-2)',
      marginBottom: 20,
    }}>
      <span style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: accent,
        boxShadow: `0 0 16px ${accent}`,
      }} />
      {children}
    </div>
  );
}

export function LogoMark({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="lg-grad" x1="0" y1="0" x2="28" y2="28" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#FFB13F" />
          <stop offset="0.5" stopColor="#A066FF" />
          <stop offset="1" stopColor="#00DFBB" />
        </linearGradient>
      </defs>
      <rect x="1" y="1" width="26" height="26" rx="8" stroke="url(#lg-grad)" strokeWidth="1.5" />
      <circle cx="10" cy="14" r="3" fill="url(#lg-grad)" />
      <circle cx="18" cy="14" r="3" fill="none" stroke="url(#lg-grad)" strokeWidth="1.5" />
      <line x1="13" y1="14" x2="15" y2="14" stroke="url(#lg-grad)" strokeWidth="1.5" />
    </svg>
  );
}
